import type {
    LocationAsRelativeRaw,
    RouteLocation,
    RouteQueryAndHash,
} from 'vue-router'
import { useRouter } from 'vue-router'
import type { RoutingRegion } from '@/plugins/region'
import {
    getRoutingRegion,
    isAppRegion,
    useRegionPlugin,
} from '@/plugins/region'
import type { ComputedRef, Ref } from 'vue'
import { computed, unref } from 'vue'
import type { RegionCode } from '@/graphql/api'
import { useReleasedUrls } from '@/plugins/released-urls/released-urls'
import { useHost } from '@/plugins/current-url'
import { useSegment } from '@/third-party/segment.ts'
import { getWindow } from '@/helpers/globals.ts'

export type AnchorTarget = '_blank' | '_self' | '_top' | '_parent'

export type ButtonRouteRelative = LocationAsRelativeRaw & RouteQueryAndHash

type ClickHandler = (e: PointerEvent) => void

export type ButtonTracking = {
    eventName: string
    param1?: string | null
    param2?: string | null
    param3?: string | null
}
// export type ButtonProps = Partial<ButtonPropsInternal>

// Do not delete please, this may be needed if we need to do `toRefs(props)` and pass into useButton
// type ButtonPropsRefs = {
//     [K in keyof ButtonProps]: Ref<ButtonProps[K]>
// }

export interface ButtonProps {
    to?: ButtonRouteRelative | string | null
    href?: string
    region?: RegionCode
    node?: 'a' | 'button'
    disabled?: boolean
    loading?: boolean
    target?: AnchorTarget
    replace?: boolean
    tracking?: ButtonTracking | null
    submit?: boolean
    clicked?: ClickHandler
}

type ButtonBind = {
    // TODO Integrate RouterLinkProps into bind
    to?: ButtonRouteRelative | string
    href?: string
    target: AnchorTarget | undefined
    disabled: boolean | undefined
    replace?: boolean
    type?: string
    onClick: (e: PointerEvent) => void
    'aria-label'?: string
}

type ButtonIs = 'RouterLink' | 'a' | 'button'

type ButtonBindings = {
    bind: ButtonBind
    is: ButtonIs
}

function useRegion(): (
    to:
        | (RouteLocation & {
              href: string
          })
        | ButtonRouteRelative,
    region: RegionCode | undefined
) => RoutingRegion {
    const { routingRegion } = useRegionPlugin()

    return (
        to:
            | (RouteLocation & {
                  href: string
              })
            | ButtonRouteRelative,
        region: RegionCode | undefined
    ): RoutingRegion => {
        if (region) {
            return getRoutingRegion(region)
        }

        const regionParam = to?.params?.['region']

        if (regionParam && typeof regionParam === 'string') {
            const r = regionParam.toUpperCase()

            if (isAppRegion(r)) {
                return getRoutingRegion(r)
            }
        }

        return routingRegion.value
    }
}

export function useRouterLink(
    to: Ref<ButtonRouteRelative>,
    region: Ref<RegionCode | undefined>
): Ref<ButtonRouteRelative> {
    const getRegion = useRegion()

    return computed(() => ({
        name: to.value.name,
        params: {
            ...to.value.params,
            region: getRegion(to.value, region.value),
        },
        query: to.value.query,
    }))
}

type EmitFunction = (event: 'click', v: PointerEvent) => void

export function useThButton(
    props: ButtonProps,
    emit: EmitFunction,
    label: string | Ref<string | undefined>
): ComputedRef<ButtonBindings> {
    const { track } = useSegment()

    const host = useHost()

    const internal = [`https://${host}`, `http://${host}`, host, '/', '#']

    if (!internal.includes('https://taylorandhart.com')) {
        internal.push('https://taylorandhart.com')
    }

    if (!internal.includes('https://www.taylorandhart.com')) {
        internal.push('https://www.taylorandhart.com')
    }

    const onClick = (e: PointerEvent) => {
        if (props.disabled || props.loading) {
            e.stopPropagation()
            e.preventDefault()

            return
        }

        if (typeof props.clicked === 'function') {
            props.clicked(e)
        }

        emit('click', e)

        if (props.tracking) {
            track(`custom_${props.tracking.eventName}`, {
                event_name: props.tracking.eventName,
                param1: props.tracking.param1,
                param2: props.tracking.param2,
                param3: props.tracking.param3,
                url: getWindow()?.location.href,
            })
        }
    }
    const isReleasedUrl = useReleasedUrls()
    const getRegion = useRegion()
    const router = useRouter()

    return computed((): ButtonBindings => {
        const to = props.to
        const target = props.target
        const disabled = props.disabled
        const replace = props.replace
        let href = props.href

        const labelValue = unref(label)?.trim()
        const ariaLabel =
            target === '_blank' && labelValue
                ? `${labelValue} - opens in new tab`
                : undefined

        if (href) {
            for (let i = 0; i < internal.length; i += 1) {
                if (href.startsWith(internal[i] as string)) {
                    if (internal[i] !== '#' && internal[i] !== '/') {
                        href = href.replace(internal[i] as string, '')
                    }

                    const route = router.resolve(href)
                    const region = getRegion(route, props.region)

                    if (region) {
                        route.params['region'] = region
                    }

                    const isReleased = isReleasedUrl(route)
                    const path = router.resolve(route).fullPath
                    const bind = isReleased ? { to: path } : { href: path }
                    const is = isReleased ? 'RouterLink' : 'a'

                    return {
                        bind: {
                            ...(disabled ? {} : bind),
                            target,
                            disabled,
                            'aria-label': ariaLabel,
                            onClick,
                        },
                        is,
                    }
                }
            }

            return {
                bind: {
                    ...(disabled ? {} : { href }),
                    target,
                    disabled,
                    'aria-label': ariaLabel,
                    onClick,
                },
                is: props.node || 'a',
            }
        }

        if (to) {
            const route = router.resolve(to)

            if (route.name) {
                const region = getRegion(route, props.region)

                if (region) {
                    route.params['region'] = region
                }

                const released = isReleasedUrl(route)
                const path = router.resolve(route).fullPath
                const bind = released ? { to: path } : { href: path }
                const is = released ? 'RouterLink' : 'a'

                if (disabled) {
                    return {
                        bind: {
                            type: 'button',
                            disabled,
                            target,
                            'aria-label': ariaLabel,
                            onClick,
                        },
                        is: 'button',
                    }
                }

                return {
                    bind: {
                        ...bind,
                        target,
                        disabled,
                        replace,
                        'aria-label': ariaLabel,
                        onClick,
                    },
                    is,
                }
            }
        }

        if (typeof to === 'string') {
            return {
                bind: {
                    href: to,
                    target,
                    disabled,
                    'aria-label': ariaLabel,
                    onClick,
                },
                is: props.node || 'a',
            }
        }

        return {
            bind: {
                disabled,
                target,
                type: props.submit ? 'submit' : 'button',
                'aria-label': ariaLabel,
                onClick,
            },
            is: props.node || 'button',
        }
    })
}
